export const BG_HACK_FONTS = ["metalmacabre", "trocadero", "endor", "lavanderia"] as const;
// EWW-470: was 4 -- web drew the background ~4px larger than the tight text box on every side.
// iOS's background is the label's own bounds with no padding at all (see `WPTextLabel.bgColor`,
// just `self.backgroundColor = bgColor`), so a background-colored text watermark's box didn't
// match between platforms. Zeroing this makes web's background hug the text exactly like iOS's
// does -- selecting a watermark still shows the same size box before and after adding a
// background color, instead of the box growing by the old padding amount.
export const DEFAULT_TEXT_BG_PADDING = 0 as const;

/**
 * Inset that the web editor bakes into a text watermark's x/y so the *visible background box*
 * edge sits flush where the user placed it (see text.tsx `wrappedSetX/Y`).
 *
 * Template save strips this inset before encoding `flexible_frame` (mobile decodes without it),
 * so the web render path must add the exact same amount back when re-applying a template --
 * otherwise the text lands `top`/`left` px off from where it was saved. Both sides MUST call
 * this one function; duplicating the font list is what previously let them drift apart.
 *
 * Deliberately separate from `getTextBackgroundInsets` (which sizes/offsets the drawn Rect):
 * that one only special-cases metalmacabre, and changing it would alter how backgrounds render.
 */
export function getTemplateTextBgInsets({
  normalizedFont,
  bgPadding,
}: {
  normalizedFont: string | undefined;
  bgPadding: number;
}): { top: number; left: number } {
  const pad = Math.max(0, Number(bgPadding) || 0);
  const f = String(normalizedFont ?? "").toLowerCase();
  const isHackFont = BG_HACK_FONTS.some((hack) => f.includes(hack));
  return {
    top: isHackFont ? pad * 8 : pad,
    left: isHackFont ? pad * 4 : pad,
  };
}

export function getTextBackgroundInsets({
  normalizedFont,
  bgPadding,
}: {
  normalizedFont: string | undefined;
  bgPadding: number;
}): { top: number; right: number; bottom: number; left: number } {
  const pad = Math.max(0, Number(bgPadding) || 0);
  const f = String(normalizedFont ?? "").toLowerCase();

  if (f.includes("metalmacabre")) {
    return {
      top: pad * 8,
      right: pad * 4,
      bottom: pad * 2,
      left: pad * 4,
    };
  }

  return {
    top: pad,
    right: pad,
    bottom: pad,
    left: pad,
  };
}

/**
 * How far a font's *painted* glyphs spill outside the metric ("em") box Konva sizes text with.
 *
 * Konva derives a Text node's box from font metrics -- width = advance width, height =
 * lines x fontSize x lineHeight. Decorative faces (swashes, exaggerated ascenders/descenders)
 * paint well outside that box, so the selection rectangle and background fill cut through the
 * glyphs. The old workaround was a hardcoded `BG_HACK_FONTS` list multiplied by 8, which only
 * ever covered fonts somebody happened to report.
 *
 * This measures the real ink extents via `TextMetrics.actualBoundingBox*` instead, so it is
 * correct for EVERY font with no list to maintain. Returns the overflow PER EDGE (not one
 * scalar) so callers can fit a box asymmetrically -- script faces spill far more vertically
 * than horizontally, and collapsing that to a single number is what made the selection look
 * loose on the narrow axis.
 *
 * Returns `null` -- not zeroes -- when the browser doesn't expose the actualBoundingBox
 * metrics, so callers can distinguish "measured, no overflow" from "couldn't measure" and
 * fall back to the legacy estimate instead of collapsing the box.
 */
let inkMeasureCanvas: HTMLCanvasElement | null = null;

/**
 * EWW-470: picks the vertical ascent/descent pair to size a line's box from -- the font's
 * DECLARED metrics (`fontBoundingBox*`) when the browser exposes them, else the real painted ink
 * (`actualBoundingBox*`).
 *
 * Mobile's `WPTextLabel` is a stock `UILabel` with no drawing override (confirmed directly in
 * `WPTextLabel.swift`), so its layout always centers text using the font's own declared
 * ascent/descent (its hhea/OS2 metrics table), never the real ink -- regardless of what bounds
 * it's given. `fontBoundingBox*` reads that same declared table from the same font file, so it's
 * the closest thing a browser exposes to what CoreText will independently compute; using the
 * real (tighter) ink here instead previously gave mobile a shorter box than what its own
 * `textRect(forBounds:)` naturally computes, which shifted the centered text rather than fixing
 * it. Horizontal stays on real ink in both callers -- only vertical follows the font's box.
 */
function lineVerticalMetrics(m: TextMetrics): { ascent: number; descent: number } {
  const fbAscent = Number(m.fontBoundingBoxAscent);
  const fbDescent = Number(m.fontBoundingBoxDescent);
  const hasFontBox = Number.isFinite(fbAscent) && Number.isFinite(fbDescent) && fbAscent + fbDescent > 0;
  return {
    ascent: hasFontBox ? fbAscent : Number(m.actualBoundingBoxAscent) || 0,
    descent: hasFontBox ? fbDescent : Number(m.actualBoundingBoxDescent) || 0,
  };
}

export function measureTextInkOverflowEdges({
  text,
  fontFamily,
  fontSize,
  lineHeight = 1,
  align = "left",
  boxWidth = 0,
}: {
  text: string | undefined;
  fontFamily: string | undefined;
  fontSize: number | undefined;
  lineHeight?: number;
  align?: string;
  boxWidth?: number;
}): { top: number; right: number; bottom: number; left: number } | null {
  const size = Number(fontSize) || 0;
  const value = String(text ?? "");
  if (!value || size <= 0 || typeof document === "undefined") return null;

  try {
    if (!inkMeasureCanvas) inkMeasureCanvas = document.createElement("canvas");
    const ctx = inkMeasureCanvas.getContext("2d");
    if (!ctx) return null;

    ctx.font = `${size}px "${fontFamily || "Arial"}"`;
    // Match Konva exactly (konva/lib/shapes/Text.js): it sets textBaseline 'middle', starts at
    // translateY = lineHeightPx / 2 and steps by lineHeightPx per line. So line i's baseline
    // sits at (i + 0.5) * lineStep in the node's own coordinates, and the measured ascent /
    // descent are relative to that baseline.
    ctx.textBaseline = "middle";

    const lineStep = size * (Number(lineHeight) || 1);
    const lines = value.split("\n");
    const boxHeight = lines.length * lineStep;
    const totalWidth = Number(boxWidth) || 0;

    let top = 0;
    let right = 0;
    let bottom = 0;
    let left = 0;

    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i];
      if (!line) continue;
      const m = ctx.measureText(line);
      if (typeof m.actualBoundingBoxAscent !== "number") return null;

      const { ascent, descent } = lineVerticalMetrics(m);
      const leftBearing = Number(m.actualBoundingBoxLeft) || 0;
      const rightExtent = Number(m.actualBoundingBoxRight) || 0;
      const advance = Number(m.width) || 0;

      const baseline = (i + 0.5) * lineStep;
      top = Math.max(top, ascent - baseline);
      bottom = Math.max(bottom, baseline + descent - boxHeight);

      // Konva applies the same per-line offset for align center/right.
      let lineX = 0;
      if (totalWidth > 0) {
        if (align === "center") lineX = (totalWidth - advance) / 2;
        else if (align === "right") lineX = totalWidth - advance;
      }
      left = Math.max(left, leftBearing - lineX);
      right = Math.max(right, lineX + rightExtent - (totalWidth || advance));
    }

    return {
      top: Math.max(0, Math.ceil(top)),
      right: Math.max(0, Math.ceil(right)),
      bottom: Math.max(0, Math.ceil(bottom)),
      left: Math.max(0, Math.ceil(left)),
    };
  } catch {
    return null;
  }
}

/**
 * EWW-470: the TIGHT painted-ink bounding box of the text, in the Konva Text node's OWN
 * coordinates (origin = the metric box's top-left), i.e. `{x, y}` is where the ink actually
 * starts inside that box and `{width, height}` is how big the ink actually is.
 *
 * Distinct from `measureTextInkOverflowEdges` above, which only reports how far ink spills
 * OUTSIDE the metric box and clamps at 0 -- it cannot express ink that is SMALLER than the box.
 * That is the case this exists for: a font's metric box reserves room for the ascenders and
 * descenders of its whole character set, so an all-caps string like "WHIZPOOL" leaves a large
 * unused band above and below the glyphs.
 *
 * Why it matters beyond web's own rendering: `content_width_ratio`/`content_height_ratio` are
 * saved from this geometry and mobile sizes its OWN label from them
 * (`FlexibleFrameUtils.getResponsiveWatermarkSize`), then paints its background over the whole
 * label and vertically centres the text inside it. Saving the metric box therefore hands mobile
 * a box taller than the text, and the difference shows up as a band of background above and
 * below the glyphs that web (which sizes its box to the text) never had.
 *
 * Returns `null` when there is nothing to measure or the browser doesn't expose the
 * `actualBoundingBox*` metrics, so callers fall back to the metric box exactly as before.
 */
export function measureTextInkBox({
  text,
  fontFamily,
  fontSize,
  lineHeight = 1,
  align = "left",
  boxWidth = 0,
}: {
  text: string | undefined;
  fontFamily: string | undefined;
  fontSize: number | undefined;
  lineHeight?: number;
  align?: string;
  boxWidth?: number;
}): { x: number; y: number; width: number; height: number } | null {
  const size = Number(fontSize) || 0;
  const value = String(text ?? "");
  if (!value || size <= 0 || typeof document === "undefined") return null;

  try {
    if (!inkMeasureCanvas) inkMeasureCanvas = document.createElement("canvas");
    const ctx = inkMeasureCanvas.getContext("2d");
    if (!ctx) return null;

    ctx.font = `${size}px "${fontFamily || "Arial"}"`;
    // Same baseline convention as `measureTextInkOverflowEdges` -- Konva sets textBaseline
    // 'middle' and puts line i's baseline at (i + 0.5) * lineStep in the node's own space.
    ctx.textBaseline = "middle";

    const lineStep = size * (Number(lineHeight) || 1);
    const lines = value.split("\n");
    const totalWidth = Number(boxWidth) || 0;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let sawGlyph = false;

    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i];
      if (!line) continue;
      const m = ctx.measureText(line);
      if (typeof m.actualBoundingBoxAscent !== "number") return null;

      const leftBearing = Number(m.actualBoundingBoxLeft) || 0;
      const rightExtent = Number(m.actualBoundingBoxRight) || 0;
      const advance = Number(m.width) || 0;
      // A line of only whitespace has no ink; it still occupies a line box, but contributes
      // nothing to the painted bounds. Checked on the REAL ink (`actualBoundingBox*`), not the
      // font's declared box used below -- `fontBoundingBoxAscent/Descent` are a property of the
      // font itself and are essentially always > 0 even for a blank line, so gating on them here
      // would count every whitespace-only line as "has ink" and skew the vertical range.
      const realAscent = Number(m.actualBoundingBoxAscent) || 0;
      const realDescent = Number(m.actualBoundingBoxDescent) || 0;
      if (realAscent + realDescent <= 0 && leftBearing + rightExtent <= 0) continue;
      sawGlyph = true;

      const { ascent, descent } = lineVerticalMetrics(m);
      const baseline = (i + 0.5) * lineStep;
      minY = Math.min(minY, baseline - ascent);
      maxY = Math.max(maxY, baseline + descent);

      // Konva applies the same per-line offset for align center/right.
      let lineX = 0;
      if (totalWidth > 0) {
        if (align === "center") lineX = (totalWidth - advance) / 2;
        else if (align === "right") lineX = totalWidth - advance;
      }
      minX = Math.min(minX, lineX - leftBearing);
      maxX = Math.max(maxX, lineX + rightExtent);
    }

    if (!sawGlyph) return null;

    const width = Math.max(0, maxX - minX);
    const height = Math.max(0, maxY - minY);
    if (!(width > 0) || !(height > 0)) return null;

    return { x: minX, y: minY, width, height };
  } catch {
    return null;
  }
}

export function needsBgHackFont(normalizedFont: string | undefined): boolean {
  const f = String(normalizedFont ?? "").toLowerCase();
  return BG_HACK_FONTS.some((v) => f.includes(v));
}

export function getSelectionPadding({
  bgFill,
  normalizedFont,
  bgPadding,
  hackMultiplier = 8,
}: {
  bgFill: unknown;
  normalizedFont: string | undefined;
  bgPadding: number;
  hackMultiplier?: number;
}): number {
  if (!bgFill) return 0;
  const pad = Number(bgPadding) || 0;
  if (pad <= 0) return 0;
  return needsBgHackFont(normalizedFont) ? pad * hackMultiplier : pad;
}
